<template>
  <header
    v-show="showHeader"
    :class="[
      'fixed top-0 left-0 right-0 z-50',
      'bg-white',
      scrolled ? 'lg:bg-black' : 'lg:bg-transparent'
    ]"
  >
    <div class="container mx-auto flex justify-between items-center px-4">
      <!-- Left-aligned SVG icon -->
      <router-link to="/">
        <img
          src="/image/83-icon.svg"
          alt="83 Icon"
          :class="[
            'w-16 h-16',
            logoBlack ? '' : 'lg:[filter:brightness(0)_invert(1)]'
          ]"
        />
      </router-link>

      <!-- Desktop Navigation -->
      <nav class="hidden lg:flex items-center space-x-8 px-4 py-2">
        <template v-for="(link, index) in navLinks" :key="index">
          <!-- Links with submenu -->
          <div
            v-if="link.submenu && link.submenu.length > 0"
            class="relative group flex items-center"
          >
            <router-link
              :to="link.to || '#'"
              active-class="text-gold"
              :class="[
                'font-maax text-sm uppercase',
                inverted && !scrolled ? 'text-black hover:text-gray-700' : 'text-white hover:text-gray-300',
                link.desktopHidden ? 'hidden' : ''
              ]"
            >
              {{ link.text }}
            </router-link>
            <!-- Submenu -->
            <div
              class="absolute top-full left-0 hidden group-hover:block pt-4"
            >
              <div class="bg-black px-4 py-2 rounded flex flex-col space-y-1 whitespace-nowrap">
                <router-link
                  v-for="(subItem, subIndex) in link.submenu"
                  :key="subIndex"
                  :to="subItem.to"
                  active-class="text-gold border-gold"
                  class="font-maax text-sm uppercase pl-4 border-l-3 border-white text-white hover:text-gold hover:border-gold"
                >
                  {{ subItem.text }}
                </router-link>
              </div>
            </div>
          </div>
          <!-- Regular links without submenu -->
          <router-link
            v-else
            v-show="!link.desktopHidden"
            :to="link.to || '#'"
            active-class="text-gold"
            :class="[
              'font-maax text-sm uppercase',
              inverted && !scrolled ? 'text-black hover:text-gray-700' : 'text-white hover:text-gray-300'
            ]"
          >
            {{ link.text }}
          </router-link>
        </template>
      </nav>

      <!-- Mobile Hamburger Button -->
      <button
        @click="toggleMenu"
        class="lg:hidden p-4 z-50 relative"
        aria-label="Toggle menu"
      >
        <div class="w-6 h-5 flex flex-col justify-between">
          <span class="block h-0.5 w-full bg-black"></span>
          <span v-if="!mobileMenuOpen" class="block h-0.5 w-full bg-black"></span>
          <span class="block h-0.5 w-full bg-black"></span>
        </div>
      </button>
    </div>

    <!-- Mobile Menu -->
    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 z-40 lg:hidden"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black bg-opacity-50"
        @click="closeMenu"
      ></div>

      <!-- Menu Panel -->
      <nav class="absolute top-0 right-0 bottom-0 w-4/5 max-w-sm bg-black overflow-y-auto">
        <div class="flex flex-col p-8 pt-24 space-y-6">
          <template v-for="(link, index) in navLinks" :key="index">
            <!-- Links with submenu -->
            <div v-if="link.submenu && link.submenu.length > 0" class="flex flex-col space-y-3">
              <router-link
                :to="link.to || '#'"
                active-class="text-gold"
                class="font-maax text-2xl uppercase text-white hover:text-gold"
                @click="closeMenu"
              >
                {{ link.text }}
              </router-link>
              <!-- Submenu items -->
              <div class="flex flex-col space-y-2 pl-4 border-l-2 border-white">
                <router-link
                  v-for="(subItem, subIndex) in link.submenu"
                  :key="subIndex"
                  :to="subItem.to"
                  active-class="text-gold"
                  class="font-maax text-lg uppercase text-white hover:text-gold"
                  @click="closeMenu"
                >
                  {{ subItem.text }}
                </router-link>
              </div>
            </div>
            <!-- Regular links without submenu -->
            <router-link
              v-else
              :to="link.to || '#'"
              active-class="text-gold"
              class="font-maax text-2xl uppercase text-white hover:text-gold"
              @click="closeMenu"
            >
              {{ link.text }}
            </router-link>
          </template>
        </div>
      </nav>
    </div>
  </header>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useScroll } from '../composables/useScroll'
import { useRoute } from 'vue-router'

const props = defineProps({
  inverted: {
    type: Boolean,
    default: false
  },
  logoBlack: {
    type: Boolean,
    default: false
  }
});

const { scrolled, showHeader } = useScroll()
const route = useRoute()
const mobileMenuOpen = ref(false)

const toggleMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
  // Lock/unlock body scroll
  if (mobileMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeMenu = () => {
  mobileMenuOpen.value = false
  document.body.style.overflow = ''
}

// Close menu on route change
watch(() => route.path, () => {
  closeMenu()
})

const navLinks = [
  {
    text: 'Discover',
    to: '/discover',
    submenu: [
      { text: 'Vanuatu Terroir', to: '/terroir' },
      { text: 'Our Restless Earth', to: '/restless-earth' },
      { text: 'Entropy Index', to: '/entropy-index' },
      { text: 'Our Team', to: '/team' },
      { text: 'News', to: '/in-the-news' }
    ]
  },
  {
    text: 'The Spirits',
    to: '/spirits',
    submenu: [
      { text: 'Collections', to: '/collections' },
      { text: 'Habitation Velier', to: '/habitation-velier' },
      { text: 'Europe Shipment', to: '/europe-shipment' },
      { text: 'The Rackhouse', to: '/rackhouse' },
      { text: 'Batch Information', to: '/batch' }
    ]
  },
  { text: 'The Team', to: '/team', desktopHidden: true },
  { text: 'Tours & Events', to: '/tours' },
  { text: 'Where to Buy', to: '/buy' },
  { text: 'Contact Us', to: '/contact' },
  { text: 'In the News', to: '/in-the-news', desktopHidden: true }
];
</script>
